'use client'

import { useState, type ChangeEvent } from 'react'
import { Calculator, TrendingUp, RotateCcw } from 'lucide-react'

// Interactive UGC ROI calculator for long-form articles. Renders from
// the `{ type: 'roi-calculator' }` ArticleBlock. The reader edits their
// own monthly sessions, baseline conversion, AOV, expected UGC
// conversion lift and Idukki monthly cost; the incremental revenue,
// annual lift, ROI multiple and payback update live. Pure client state,
// nothing is sent anywhere. Estimate only.
//
// Design: matches the infographic blocks (rounded-2xl, bg-card,
// font-display, design-token colours). No animation beyond colour
// transitions, so it is reduced-motion safe by construction.

type Field = {
  key: 'sessions' | 'cvr' | 'aov' | 'lift' | 'cost'
  label: string
  min: number
  max: number
  step: number
  prefix?: string
  suffix?: string
}

const FIELDS: Field[] = [
  { key: 'sessions', label: 'Monthly store visitors', min: 1000, max: 500000, step: 1000 },
  { key: 'cvr', label: 'Baseline conversion rate', min: 0.2, max: 8, step: 0.1, suffix: '%' },
  { key: 'aov', label: 'Average order value', min: 10, max: 500, step: 1, prefix: '$' },
  { key: 'lift', label: 'Expected UGC conversion lift', min: 0, max: 40, step: 1, suffix: '%' },
  { key: 'cost', label: 'Idukki cost / month', min: 0, max: 2000, step: 25, prefix: '$' },
]

const DEFAULTS = { sessions: 60000, cvr: 1.8, aov: 64, lift: 12, cost: 199 }

function fmtMoney(n: number): string {
  if (!isFinite(n)) return '—'
  if (Math.abs(n) >= 1000) return '$' + Math.round(n).toLocaleString('en-US')
  return '$' + Math.round(n).toLocaleString('en-US')
}

function fmtInput(f: Field, v: number): string {
  const body = f.step < 1 ? v.toFixed(1) : Math.round(v).toLocaleString('en-US')
  return `${f.prefix ?? ''}${body}${f.suffix ?? ''}`
}

export function ArticleRoiCalculator({ title, caption }: { title?: string; caption?: string }) {
  const [vals, setVals] = useState<typeof DEFAULTS>(DEFAULTS)

  const baseOrders = vals.sessions * (vals.cvr / 100)
  const liftedOrders = vals.sessions * ((vals.cvr * (1 + vals.lift / 100)) / 100)
  const incrementalOrders = liftedOrders - baseOrders
  const incrementalMonthly = incrementalOrders * vals.aov
  const incrementalAnnual = incrementalMonthly * 12
  const roiMultiple = vals.cost > 0 ? incrementalMonthly / vals.cost : Infinity
  const paybackDays = incrementalMonthly > 0 ? (vals.cost / incrementalMonthly) * 30 : Infinity

  const set = (k: Field['key'], v: number) => setVals((s: typeof DEFAULTS) => ({ ...s, [k]: v }))
  const reset = () => setVals(DEFAULTS)

  return (
    <figure className="not-prose my-10 overflow-hidden rounded-2xl border bg-card">
      <div className="flex items-start justify-between gap-3 border-b bg-muted/30 px-5 py-4 md:px-7">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/12 text-primary">
            <Calculator className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <p className="font-display text-sm font-semibold tracking-tight md:text-base">
              {title || 'Calculate your UGC ROI'}
            </p>
            {caption && <p className="mt-1 text-xs leading-relaxed text-muted-foreground md:text-sm">{caption}</p>}
          </div>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium text-muted-foreground motion-safe:transition hover:border-primary/40 hover:text-foreground"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Reset
        </button>
      </div>

      <div className="grid gap-6 p-5 md:grid-cols-[1.1fr_0.9fr] md:p-7">
        {/* Inputs */}
        <div className="flex flex-col gap-5">
          {FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className="flex items-baseline justify-between">
                <span className="text-sm font-medium text-foreground/80">{f.label}</span>
                <span className="font-display text-sm font-semibold tabular-nums text-primary">
                  {fmtInput(f, vals[f.key])}
                </span>
              </span>
              <input
                type="range"
                min={f.min}
                max={f.max}
                step={f.step}
                value={vals[f.key]}
                onChange={(e: ChangeEvent<HTMLInputElement>) => set(f.key, Number(e.target.value))}
                aria-label={f.label}
                className="mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-full bg-primary/15 accent-primary"
              />
            </label>
          ))}
        </div>

        {/* Outputs */}
        <div className="flex flex-col gap-4 rounded-xl border bg-gradient-to-br from-primary/[0.06] via-card to-card p-5">
          <div className="flex items-center gap-2 text-primary">
            <TrendingUp className="h-4 w-4" aria-hidden />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em]">Estimated impact</span>
          </div>

          <div>
            <p className="font-display text-3xl font-semibold leading-none tracking-tight tabular-nums md:text-4xl">
              {fmtMoney(incrementalMonthly)}
            </p>
            <p className="mt-1.5 text-xs text-muted-foreground">incremental revenue / month</p>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t pt-4">
            <div>
              <p className="font-display text-lg font-semibold tabular-nums">{fmtMoney(incrementalAnnual)}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">added / year</p>
            </div>
            <div>
              <p className="font-display text-lg font-semibold tabular-nums">
                {Math.round(incrementalOrders).toLocaleString('en-US')}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">extra orders / month</p>
            </div>
            <div>
              <p className="font-display text-lg font-semibold tabular-nums">
                {isFinite(roiMultiple) ? `${roiMultiple.toFixed(1)}×` : '∞'}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">return on cost</p>
            </div>
            <div>
              <p className="font-display text-lg font-semibold tabular-nums">
                {isFinite(paybackDays) ? `${Math.max(1, Math.round(paybackDays))} days` : '—'}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">payback period</p>
            </div>
          </div>
        </div>
      </div>

      <figcaption className="border-t px-5 py-3 text-[11px] leading-relaxed text-muted-foreground md:px-7">
        Estimate only. Assumes the conversion lift applies to existing traffic; it does not model seasonality,
        margin or returns. Lift ranges are drawn from public UGC benchmarks (Bazaarvoice, Nosto); your numbers will vary.
      </figcaption>
    </figure>
  )
}
